import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, AlertCircle, Youtube, Link, Clapperboard, Search, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AudioFile, DropboxUploadDraft } from '../types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import { sessionService, type EpisodeFamilyListItem } from '../services/sessionService';
import DropboxIcon from './DropboxIcon';
import ProductionMetadataFields from './ProductionMetadataFields';

const KNOWN_SHOW_OPTIONS = [
  { slug: 'mlst', title: 'Machine Learning Street Talk' },
  { slug: 'mindscape', title: 'Mindscape' },
  { slug: 'dwarkesh-patel', title: 'Dwarkesh Patel' },
  { slug: 'theories-of-everything', title: 'Theories of Everything' },
];

const CUSTOM_SHOW_OPTION = '__custom__';

type SeedEpisodeCandidate = Awaited<ReturnType<typeof sessionService.listSessions>>[number];

interface FileUploadProps {
  onFileSelect: (file: AudioFile) => void;
  onYouTubeSelect: (url: string, cookies?: string) => void;
  onDropboxSelect: (draft: DropboxUploadDraft) => void;
  showAssignment: {
    showSlug: string;
    showTitle: string;
  };
  onShowAssignmentChange: (assignment: { showSlug: string; showTitle: string }) => void;
  familyAssignment: {
    familySlug: string;
    familyTitle: string;
    familyVersionNumber: number | null;
    seedSessionId: string;
    seedSessionTitle: string;
  };
  onFamilyAssignmentChange: (assignment: {
    familySlug: string;
    familyTitle: string;
    familyVersionNumber: number | null;
    seedSessionId: string;
    seedSessionTitle: string;
  }) => void;
  isProcessing?: boolean;
  className?: string;
}

const DROPBOX_URL_REGEX = /^(https?:\/\/)?(www\.)?dropbox\.com\/(s|scl\/fi)\/.+/i;

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onYouTubeSelect,
  onDropboxSelect,
  showAssignment,
  onShowAssignmentChange,
  familyAssignment,
  onFamilyAssignmentChange,
  isProcessing = false,
  className = '',
}) => {
  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const [selectedFile, setSelectedFile] = useState<AudioFile | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [selectedYoutubeUrl, setSelectedYoutubeUrl] = useState<string>('');
  const [selectedDropboxDraft, setSelectedDropboxDraft] = useState<DropboxUploadDraft | null>(null);
  const [error, setError] = useState<string>('');
  const [inputMode, setInputMode] = useState<'file' | 'youtube' | 'dropbox'>('file');
  const [isComposerExpanded, setIsComposerExpanded] = useState(false);
  const [youtubeCookies, setYoutubeCookies] = useState<string>('');
  const [showCookiesInput, setShowCookiesInput] = useState<boolean>(false);
  const [familyMode, setFamilyMode] = useState<'standalone' | 'existing' | 'new'>(
    familyAssignment.seedSessionId ? 'new' : familyAssignment.familySlug ? 'existing' : 'standalone'
  );
  const [selectedFamilySlug, setSelectedFamilySlug] = useState<string>(familyAssignment.familySlug || '');
  const [selectedSeedSessionId, setSelectedSeedSessionId] = useState<string>(familyAssignment.seedSessionId || '');
  const [availableFamilies, setAvailableFamilies] = useState<EpisodeFamilyListItem[]>([]);
  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false);
  const [familyLoadError, setFamilyLoadError] = useState('');
  const [seedSearchTerm, setSeedSearchTerm] = useState<string>('');
  const [seedCandidates, setSeedCandidates] = useState<SeedEpisodeCandidate[]>([]);
  const [isLoadingSeedCandidates, setIsLoadingSeedCandidates] = useState(false);
  const [seedCandidateError, setSeedCandidateError] = useState('');
  const knownShowSlugSet = new Set(KNOWN_SHOW_OPTIONS.map((option) => option.slug));
  const selectedShowOption = knownShowSlugSet.has(showAssignment.showSlug)
    ? showAssignment.showSlug
    : CUSTOM_SHOW_OPTION;
  const [dropboxDraft, setDropboxDraft] = useState<DropboxUploadDraft>({
    dropboxVideoUrl: '',
    localVideoPath: '',
    timelineName: '',
    dropboxDrpUrl: '',
    videoFilename: '',
    productionNotes: '',
    autoPrepareVideoSource: true,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const loadFamilies = async () => {
      setIsLoadingFamilies(true);
      setFamilyLoadError('');
      try {
        const families = await sessionService.listEpisodeFamilies(token);
        if (!cancelled) {
          setAvailableFamilies(families);
        }
      } catch (err) {
        if (!cancelled) {
          setFamilyLoadError(err instanceof Error ? err.message : 'Failed to load episode families');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingFamilies(false);
        }
      }
    };
    void loadFamilies();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || familyMode !== 'new' || !showAssignment.showSlug.trim()) {
      setSeedCandidates([]);
      setSeedCandidateError('');
      setIsLoadingSeedCandidates(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setIsLoadingSeedCandidates(true);
      setSeedCandidateError('');
      try {
        const sessions = await sessionService.listSessions(
          token,
          seedSearchTerm.trim() || undefined,
          25,
          'all',
          {
            show: showAssignment.showSlug,
            contentSource: 'all',
            contentVisibility: 'all',
            includeExternal: true,
          }
        );
        if (!cancelled) {
          setSeedCandidates(
            sessions.filter((session) => !session.family && !session.isDeprecated)
          );
        }
      } catch (err) {
        if (!cancelled) {
          setSeedCandidateError(err instanceof Error ? err.message : 'Failed to load seed episodes');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSeedCandidates(false);
        }
      }
    }, seedSearchTerm ? 250 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [token, familyMode, seedSearchTerm, showAssignment.showSlug]);

  const filteredFamilies = useMemo(
    () => availableFamilies.filter((family) => family.showSlug === showAssignment.showSlug),
    [availableFamilies, showAssignment.showSlug]
  );

  const slugifyFamilyValue = useCallback((value: string): string => (
    value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '')
  ), []);

  const normalizeFamilyBaseTitle = useCallback((value: string): string => {
    const cleaned = value
      .replace(/^\[[^\]]*deprecated[^\]]*\]\s*/i, '')
      .replace(/\b(?:S|P)\d+(?:\.\d+)?\b/gi, '')
      .replace(/\bv\d+\b/gi, '')
      .replace(/\((?:patreon version|release version|video-synced|synced video repair)\)/gi, '')
      .replace(/\s+-\s+(?:synced video repair|video-synced)\s*$/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return cleaned || value.trim();
  }, []);

  const deriveUniqueFamilySlug = useCallback((seedTitle: string, seedSessionId: string): string => {
    const base = slugifyFamilyValue(normalizeFamilyBaseTitle(seedTitle)) || `family-${seedSessionId.slice(0, 8)}`;
    const usedSlugs = new Set(availableFamilies.map((family) => family.slug));
    let candidate = base;
    let suffix = 2;
    while (usedSlugs.has(candidate)) {
      candidate = `${base}-${suffix}`;
      suffix += 1;
    }
    return candidate;
  }, [availableFamilies, normalizeFamilyBaseTitle, slugifyFamilyValue]);

  const computeNextFamilyVersion = useCallback((family: EpisodeFamilyListItem) => {
    const maxVersion = family.members.reduce((currentMax, member) => {
      const memberVersion = Number(member.versionNumber || 0);
      return Number.isFinite(memberVersion) ? Math.max(currentMax, memberVersion) : currentMax;
    }, 0);
    return maxVersion + 1;
  }, []);

  const resetSelection = () => {
    setSelectedFile(null);
    setSelectedYoutubeUrl('');
    setSelectedDropboxDraft(null);
  };

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setError('');

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some((dropError: any) => dropError.code === 'file-too-large')) {
        setError('File size must be less than 1GB');
      } else if (rejection.errors.some((dropError: any) => dropError.code === 'file-invalid-type')) {
        setError('Please select an audio file (MP3, WAV, M4A, FLAC, MP4, WEBM)');
      } else {
        setError('Invalid file selected');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const audioFile: AudioFile = {
        file,
        name: file.name,
        size: file.size,
        url: '',
      };

      setSelectedFile(audioFile);
      setSelectedYoutubeUrl('');
      setSelectedDropboxDraft(null);
      onFileSelect(audioFile);
    }
  }, [onFileSelect]);

  const validateYoutubeUrl = (url: string): boolean => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const handleYoutubeSubmit = () => {
    setError('');

    if (!youtubeUrl.trim()) {
      setError('Please enter a YouTube URL');
      return;
    }

    if (!validateYoutubeUrl(youtubeUrl)) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    setSelectedYoutubeUrl(youtubeUrl.trim());
    setSelectedFile(null);
    setSelectedDropboxDraft(null);
    onYouTubeSelect(youtubeUrl.trim(), youtubeCookies.trim() || undefined);
  };

  const handleDropboxSubmit = () => {
    setError('');

    if (!dropboxDraft.dropboxVideoUrl.trim()) {
      setError('Please enter the Dropbox video URL');
      return;
    }

    if (!DROPBOX_URL_REGEX.test(dropboxDraft.dropboxVideoUrl.trim())) {
      setError('Please enter a valid Dropbox share URL');
      return;
    }

    if (!dropboxDraft.localVideoPath.trim()) {
      setError('Please provide the local video path');
      return;
    }

    if (!dropboxDraft.timelineName.trim()) {
      setError('Please provide the timeline name');
      return;
    }

    if (!dropboxDraft.dropboxDrpUrl.trim()) {
      setError('Please provide the DRP Dropbox URL');
      return;
    }

    const normalizedDraft: DropboxUploadDraft = {
      ...dropboxDraft,
      dropboxVideoUrl: dropboxDraft.dropboxVideoUrl.trim(),
      localVideoPath: dropboxDraft.localVideoPath.trim(),
      timelineName: dropboxDraft.timelineName.trim(),
      dropboxDrpUrl: dropboxDraft.dropboxDrpUrl.trim(),
      videoFilename: dropboxDraft.videoFilename?.trim() || '',
      productionNotes: dropboxDraft.productionNotes?.trim() || '',
      autoPrepareVideoSource: dropboxDraft.autoPrepareVideoSource !== false,
    };

    setSelectedFile(null);
    setSelectedYoutubeUrl('');
    setSelectedDropboxDraft(normalizedDraft);
    onDropboxSelect(normalizedDraft);
  };

  const handleModeSwitch = (mode: 'file' | 'youtube' | 'dropbox') => {
    const shouldCollapse = mode === inputMode && isComposerExpanded && !isProcessing && !(selectedFile || selectedYoutubeUrl || selectedDropboxDraft);
    setInputMode(mode);
    setError('');
    resetSelection();
    setYoutubeUrl('');
    setIsComposerExpanded(!shouldCollapse);
    if (mode === 'file') {
      setShowCookiesInput(false);
    }
  };

  const handleShowOptionChange = (value: string) => {
    if (value === CUSTOM_SHOW_OPTION) {
      if (knownShowSlugSet.has(showAssignment.showSlug)) {
        setFamilyMode('standalone');
        setSelectedFamilySlug('');
        setSelectedSeedSessionId('');
        clearFamilyAssignment();
      }
      onShowAssignmentChange({
        showSlug: knownShowSlugSet.has(showAssignment.showSlug) ? '' : showAssignment.showSlug,
        showTitle: knownShowSlugSet.has(showAssignment.showSlug) ? '' : showAssignment.showTitle,
      });
      return;
    }

    const selected = KNOWN_SHOW_OPTIONS.find((option) => option.slug === value);
    if (!selected) return;
    if (selected.slug !== showAssignment.showSlug) {
      setFamilyMode('standalone');
      setSelectedFamilySlug('');
      setSelectedSeedSessionId('');
      clearFamilyAssignment();
    }
    onShowAssignmentChange({
      showSlug: selected.slug,
      showTitle: selected.title,
    });
  };

  const handleCustomShowFieldChange = (field: 'showSlug' | 'showTitle', value: string) => {
    if (field === 'showSlug' && value !== showAssignment.showSlug) {
      setFamilyMode('standalone');
      setSelectedFamilySlug('');
      setSelectedSeedSessionId('');
      clearFamilyAssignment();
    }
    onShowAssignmentChange({
      ...showAssignment,
      [field]: value,
    });
  };

  const clearFamilyAssignment = useCallback(() => {
    onFamilyAssignmentChange({
      familySlug: '',
      familyTitle: '',
      familyVersionNumber: null,
      seedSessionId: '',
      seedSessionTitle: '',
    });
  }, [onFamilyAssignmentChange]);

  const handleFamilyModeChange = (nextMode: 'standalone' | 'existing' | 'new') => {
    setFamilyMode(nextMode);
    if (nextMode === 'standalone') {
      setSelectedFamilySlug('');
      clearFamilyAssignment();
      return;
    }
    if (nextMode === 'existing') {
      setSelectedSeedSessionId('');
      if (selectedFamilySlug) {
        const existingFamily = availableFamilies.find((family) => family.slug === selectedFamilySlug);
        if (existingFamily) {
          onFamilyAssignmentChange({
            familySlug: existingFamily.slug,
            familyTitle: existingFamily.title || '',
            familyVersionNumber: computeNextFamilyVersion(existingFamily),
            seedSessionId: '',
            seedSessionTitle: '',
          });
          return;
        }
      }
      clearFamilyAssignment();
      return;
    }

    setSelectedFamilySlug('');
    setSelectedSeedSessionId(familyAssignment.seedSessionId || '');
    onFamilyAssignmentChange({
      familySlug: familyAssignment.familySlug || '',
      familyTitle: familyAssignment.familyTitle || '',
      familyVersionNumber: familyAssignment.familyVersionNumber || 2,
      seedSessionId: familyAssignment.seedSessionId || '',
      seedSessionTitle: familyAssignment.seedSessionTitle || '',
    });
  };

  const handleExistingFamilySelect = (slug: string) => {
    setSelectedFamilySlug(slug);
    const family = availableFamilies.find((entry) => entry.slug === slug);
    if (!family) {
      clearFamilyAssignment();
      return;
    }
    onFamilyAssignmentChange({
      familySlug: family.slug,
      familyTitle: family.title || '',
      familyVersionNumber: computeNextFamilyVersion(family),
      seedSessionId: '',
      seedSessionTitle: '',
    });
  };

  const handleNewFamilyFieldChange = (
    field: 'familySlug' | 'familyTitle' | 'familyVersionNumber',
    value: string
  ) => {
    if (field === 'familyVersionNumber') {
      const parsed = Number.parseInt(value, 10);
      onFamilyAssignmentChange({
        ...familyAssignment,
        familyVersionNumber: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
      });
      return;
    }
    onFamilyAssignmentChange({
      ...familyAssignment,
      [field]: value,
    });
  };

  const handleSeedSessionSelect = (session: SeedEpisodeCandidate) => {
    const familyTitle = normalizeFamilyBaseTitle(session.title);
    setSelectedSeedSessionId(session.sessionId);
    onFamilyAssignmentChange({
      familySlug: deriveUniqueFamilySlug(session.title, session.sessionId),
      familyTitle,
      familyVersionNumber: 2,
      seedSessionId: session.sessionId,
      seedSessionTitle: session.title,
    });
  };

  useEffect(() => {
    if (familyMode !== 'existing' || !selectedFamilySlug) return;
    const matchingFamily = filteredFamilies.find((family) => family.slug === selectedFamilySlug);
    if (!matchingFamily) {
      setSelectedFamilySlug('');
      clearFamilyAssignment();
      return;
    }
    onFamilyAssignmentChange({
      familySlug: matchingFamily.slug,
      familyTitle: matchingFamily.title || '',
      familyVersionNumber: familyAssignment.familyVersionNumber || computeNextFamilyVersion(matchingFamily),
      seedSessionId: '',
      seedSessionTitle: '',
    });
  }, [
    clearFamilyAssignment,
    computeNextFamilyVersion,
    familyAssignment.familyVersionNumber,
    familyMode,
    filteredFamilies,
    onFamilyAssignmentChange,
    selectedFamilySlug,
    familyAssignment.seedSessionId,
    familyAssignment.seedSessionTitle,
  ]);

  useEffect(() => {
    if (familyMode !== 'new') return;
    if (!selectedSeedSessionId) {
      if (familyAssignment.familySlug || familyAssignment.familyTitle || familyAssignment.familyVersionNumber || familyAssignment.seedSessionId) {
        clearFamilyAssignment();
      }
      return;
    }

    const selectedSeed = seedCandidates.find((candidate) => candidate.sessionId === selectedSeedSessionId);
    if (selectedSeed) {
      const nextAssignment = {
        ...familyAssignment,
        familySlug: familyAssignment.familySlug || deriveUniqueFamilySlug(selectedSeed.title, selectedSeed.sessionId),
        familyTitle: familyAssignment.familyTitle || normalizeFamilyBaseTitle(selectedSeed.title),
        familyVersionNumber: familyAssignment.familyVersionNumber || 2,
        seedSessionId: selectedSeed.sessionId,
        seedSessionTitle: selectedSeed.title,
      };
      if (
        nextAssignment.familySlug !== familyAssignment.familySlug
        || nextAssignment.familyTitle !== familyAssignment.familyTitle
        || nextAssignment.familyVersionNumber !== familyAssignment.familyVersionNumber
        || nextAssignment.seedSessionId !== familyAssignment.seedSessionId
        || nextAssignment.seedSessionTitle !== familyAssignment.seedSessionTitle
      ) {
        onFamilyAssignmentChange(nextAssignment);
      }
    }
  }, [
    clearFamilyAssignment,
    deriveUniqueFamilySlug,
    familyAssignment,
    familyMode,
    normalizeFamilyBaseTitle,
    onFamilyAssignmentChange,
    seedCandidates,
    selectedSeedSessionId,
  ]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.flac', '.mp4', '.webm', '.ogg'],
    },
    maxSize: 1024 * 1024 * 1024,
    multiple: false,
    disabled: isProcessing,
  });

  const removeSelection = () => {
    resetSelection();
    setError('');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const hasSelection = !!(selectedFile || selectedYoutubeUrl || selectedDropboxDraft);

  useEffect(() => {
    if (hasSelection || isProcessing) {
      setIsComposerExpanded(true);
    }
  }, [hasSelection, isProcessing]);

  const composerShellClassName = isDarkMode
    ? 'overflow-hidden rounded-[30px] border border-[#344052] bg-[linear-gradient(180deg,rgba(24,34,49,0.94),rgba(15,23,36,0.86))] text-[#F4F4F4] shadow-[0_32px_70px_-42px_rgba(3,8,18,0.92)] backdrop-blur-2xl'
    : 'overflow-hidden rounded-[30px] border border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.84),rgba(248,248,247,0.76))] text-[#232E3F] shadow-[0_34px_72px_-40px_rgba(35,46,63,0.3)] backdrop-blur-2xl';
  const composerPanelClassName = isDarkMode
    ? 'rounded-[24px] border border-white/[0.08] bg-white/[0.05] backdrop-blur-xl'
    : 'rounded-[24px] border border-[#D6DCE4] bg-white/[0.62] backdrop-blur-xl';
  const composerInputClassName = isDarkMode
    ? 'rounded-2xl border border-white/[0.12] bg-white/[0.08] px-3 py-2 text-sm text-[#F4F4F4] shadow-sm outline-none transition focus:border-[#FFB52B]/45'
    : 'rounded-2xl border border-[#D6DCE4] bg-white/[0.92] px-3 py-2 text-sm text-[#232E3F] shadow-sm outline-none transition focus:border-[#FFB52B]/50';
  const composerSelectClassName = `${composerInputClassName} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m2%204%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat pr-9 ${isDarkMode ? 'dark:[color-scheme:dark]' : ''}`;
  const composerMutedTextClassName = isDarkMode ? 'text-[#A9B4C3]' : 'text-[#5E6B7D]';
  const composerSelectStyle = isDarkMode ? { colorScheme: 'dark' as const } : undefined;
  const composerOptionStyle = isDarkMode
    ? { backgroundColor: '#182231', color: '#F4F4F4' }
    : undefined;
  const modeOptions: Array<{
    id: 'file' | 'youtube' | 'dropbox';
    label: string;
    icon: React.ReactNode;
  }> = [
    { id: 'file', label: 'Upload File', icon: <Upload size={18} /> },
    { id: 'youtube', label: 'YouTube URL', icon: <Youtube size={18} /> },
    { id: 'dropbox', label: 'Dropbox Video', icon: <DropboxIcon size={18} className="text-brand-yellow" /> },
  ];
  const selectedItemTitle = selectedFile
    ? selectedFile.name
    : selectedDropboxDraft
      ? (selectedDropboxDraft.videoFilename || 'Dropbox video session')
      : selectedYoutubeUrl
        ? 'YouTube video'
        : '';
  const selectedItemDetail = selectedFile
    ? formatFileSize(selectedFile.size)
    : selectedDropboxDraft
      ? selectedDropboxDraft.dropboxVideoUrl
      : selectedYoutubeUrl;

  return (
    <div className={`w-full ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className={composerShellClassName}>
          <div className="p-5 sm:p-6">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <p className={`inline-flex items-center gap-2 font-headlines text-[10px] font-semibold uppercase tracking-[0.26em] ${isDarkMode ? 'text-[#FFCC66]' : 'text-[#A36A00]'}`}>
                    <Sparkles className="h-3.5 w-3.5" />
                    New Session
                  </p>
                  <h3 className="mt-3 font-headlines text-2xl font-bold tracking-tight">
                    Upload only when you need to
                  </h3>
                  <p className={`mt-2 max-w-2xl font-body text-sm leading-6 ${composerMutedTextClassName}`}>
                    Most of the time this page is for browsing the archive. Pick a source when you want to create a new session, and the full ingest controls will open underneath.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (isProcessing) return;
                    setIsComposerExpanded((current) => !current);
                  }}
                  className={`inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2 font-headlines text-sm font-semibold transition ${
                    isDarkMode
                      ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4] hover:bg-white/[0.14]'
                      : 'border-[#D6DCE4] bg-white/[0.7] text-[#232E3F] hover:bg-white'
                  }`}
                >
                  {isComposerExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {isComposerExpanded ? 'Hide uploader' : 'Open uploader'}
                </button>
              </div>

              <div className="grid gap-2 md:grid-cols-3">
                {modeOptions.map((option) => {
                  const isActive = inputMode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleModeSwitch(option.id)}
                      disabled={isProcessing}
                      className={`flex items-center justify-center gap-2 rounded-[20px] border px-4 py-3 font-headlines text-sm font-semibold transition ${
                        isActive
                          ? 'border-[#FFB52B]/40 bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.9)]'
                          : isDarkMode
                            ? 'border-white/[0.08] bg-white/[0.05] text-[#D5DEEA] hover:bg-white/[0.1]'
                            : 'border-[#D6DCE4] bg-white/[0.58] text-[#4E5D72] hover:bg-white/[0.82]'
                      }`}
                    >
                      {option.icon}
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>

              {!isComposerExpanded && !hasSelection ? (
                <div className={`rounded-[22px] border px-4 py-3 font-body text-sm ${composerPanelClassName} ${composerMutedTextClassName}`}>
                  Choose a source above when you want to ingest a new session. Show assignment and family controls stay tucked away until then.
                </div>
              ) : null}

              <AnimatePresence initial={false}>
                {isComposerExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -8 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0, y: -8 }}
                    transition={{ duration: 0.22 }}
                    className="space-y-5 overflow-hidden"
                  >
                    <div className={`${composerPanelClassName} p-4 sm:p-5`}>
                      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
                        <div className="space-y-3">
                          <div>
                            <p className="font-headlines text-sm font-semibold">Show assignment</p>
                            <p className={`mt-1 font-body text-xs leading-5 ${composerMutedTextClassName}`}>
                              Required. Every new session must belong to a show.
                            </p>
                          </div>
                          <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                            <label className="flex flex-col gap-1.5">
                              <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Show</span>
                              <select
                                value={selectedShowOption}
                                onChange={(event) => handleShowOptionChange(event.target.value)}
                                disabled={isProcessing}
                                className={composerSelectClassName}
                                style={composerSelectStyle}
                              >
                                {KNOWN_SHOW_OPTIONS.map((option) => (
                                  <option key={option.slug} value={option.slug} style={composerOptionStyle}>
                                    {option.title}
                                  </option>
                                ))}
                                <option value={CUSTOM_SHOW_OPTION} style={composerOptionStyle}>Custom show...</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1.5">
                              <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Show slug</span>
                              <input
                                type="text"
                                value={showAssignment.showSlug}
                                onChange={(event) => handleCustomShowFieldChange('showSlug', event.target.value)}
                                disabled={isProcessing || selectedShowOption !== CUSTOM_SHOW_OPTION}
                                placeholder="mlst"
                                className={`${composerInputClassName} disabled:cursor-not-allowed disabled:opacity-55`}
                              />
                            </label>
                          </div>
                          {selectedShowOption === CUSTOM_SHOW_OPTION && (
                            <label className="flex flex-col gap-1.5">
                              <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Show title</span>
                              <input
                                type="text"
                                value={showAssignment.showTitle}
                                onChange={(event) => handleCustomShowFieldChange('showTitle', event.target.value)}
                                disabled={isProcessing}
                                placeholder="Custom show title"
                                className={composerInputClassName}
                              />
                            </label>
                          )}
                        </div>

                        <div className={`rounded-[22px] border p-4 ${isDarkMode ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#D6DCE4] bg-[#F8F8F7]/[0.78]'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-headlines text-sm font-semibold">Episode family</p>
                              <p className={`mt-1 font-body text-xs leading-5 ${composerMutedTextClassName}`}>
                                Optional. Use this for another version of an existing episode instead of putting `S0` or `v2` into the title.
                              </p>
                            </div>
                            {isLoadingFamilies ? (
                              <span className={`font-body text-xs ${composerMutedTextClassName}`}>Loading families…</span>
                            ) : null}
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            {([
                              { id: 'standalone', label: 'Standalone' },
                              { id: 'existing', label: 'Existing family' },
                              { id: 'new', label: 'New family' },
                            ] as const).map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => handleFamilyModeChange(option.id)}
                                disabled={isProcessing}
                                className={`rounded-full border px-3 py-1.5 font-headlines text-[11px] font-semibold uppercase tracking-[0.14em] transition ${
                                  familyMode === option.id
                                    ? 'border-[#FFB52B]/40 bg-[#FFB52B] text-[#232E3F]'
                                    : isDarkMode
                                      ? 'border-white/[0.08] bg-white/[0.06] text-[#D5DEEA] hover:bg-white/[0.1]'
                                      : 'border-[#D6DCE4] bg-white/[0.72] text-[#4E5D72] hover:bg-white'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>

                          {familyLoadError ? (
                            <p className="mt-3 text-xs text-red-500 dark:text-red-300">{familyLoadError}</p>
                          ) : null}

                          {familyMode === 'existing' && (
                            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1.5fr)_160px]">
                              <label className="flex flex-col gap-1.5">
                                <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Existing family</span>
                                <select
                                  value={selectedFamilySlug}
                                  onChange={(event) => handleExistingFamilySelect(event.target.value)}
                                  disabled={isProcessing || filteredFamilies.length === 0}
                                  className={`${composerSelectClassName} disabled:cursor-not-allowed disabled:opacity-55`}
                                  style={composerSelectStyle}
                                >
                                  <option value="" style={composerOptionStyle}>
                                    {filteredFamilies.length === 0 ? 'No families for this show yet' : 'Choose an existing family'}
                                  </option>
                                  {filteredFamilies.map((family) => (
                                    <option key={family.slug} value={family.slug} style={composerOptionStyle}>
                                      {family.title || family.slug} · {family.versionCount} versions
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label className="flex flex-col gap-1.5">
                                <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Version</span>
                                <input
                                  type="number"
                                  min={1}
                                  value={familyAssignment.familyVersionNumber ?? ''}
                                  onChange={(event) => handleNewFamilyFieldChange('familyVersionNumber', event.target.value)}
                                  disabled={isProcessing || !selectedFamilySlug}
                                  className={`${composerInputClassName} disabled:cursor-not-allowed disabled:opacity-55`}
                                />
                              </label>
                            </div>
                          )}

                          {familyMode === 'new' && (
                            <div className="mt-4 space-y-4">
                              <div className={`rounded-[20px] border p-4 ${isDarkMode ? 'border-white/[0.08] bg-[#101925]/70' : 'border-[#D6DCE4] bg-white/[0.7]'}`}>
                                <div className={`mb-2 flex items-center gap-2 font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>
                                  <Search size={14} />
                                  Seed from existing episode
                                </div>
                                <p className={`mb-3 font-body text-sm leading-6 ${composerMutedTextClassName}`}>
                                  Pick the earlier standalone session that this upload is replacing or refining. The seed becomes version 1 and the new upload defaults to version 2.
                                </p>
                                <div className="relative">
                                  <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${composerMutedTextClassName}`} />
                                  <input
                                    type="text"
                                    value={seedSearchTerm}
                                    onChange={(event) => setSeedSearchTerm(event.target.value)}
                                    disabled={isProcessing}
                                    placeholder={`Search ${showAssignment.showTitle} episodes`}
                                    className={`w-full pl-9 ${composerInputClassName}`}
                                  />
                                </div>
                                {seedCandidateError ? (
                                  <p className="mt-3 text-xs text-red-500 dark:text-red-300">{seedCandidateError}</p>
                                ) : null}
                                <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                                  {isLoadingSeedCandidates ? (
                                    <p className={`font-body text-sm ${composerMutedTextClassName}`}>Loading seed episodes…</p>
                                  ) : seedCandidates.length === 0 ? (
                                    <p className={`font-body text-sm ${composerMutedTextClassName}`}>No standalone sessions found for this show yet.</p>
                                  ) : (
                                    seedCandidates.map((session) => {
                                      const isSelected = session.sessionId === selectedSeedSessionId;
                                      const seedMetaClassName = isSelected
                                        ? (isDarkMode ? 'text-[#FFD88A]' : 'text-[#5E4A16]')
                                        : composerMutedTextClassName;
                                      return (
                                        <button
                                          key={session.sessionId}
                                          type="button"
                                          onClick={() => handleSeedSessionSelect(session)}
                                          disabled={isProcessing}
                                          className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                                            isSelected
                                              ? 'border-[#FFB52B]/40 bg-[#FFF4D9] text-[#232E3F] dark:bg-[#FFB52B]/12 dark:text-[#FFE3A1]'
                                              : isDarkMode
                                                ? 'border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08]'
                                                : 'border-[#D6DCE4] bg-white/[0.7] hover:bg-white'
                                          }`}
                                        >
                                          <div className="text-sm font-semibold">{session.title}</div>
                                          <div className={`mt-1 font-body text-xs ${seedMetaClassName}`}>
                                            {session.sessionId} · updated {new Date(session.updatedAt).toLocaleDateString()}
                                          </div>
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              </div>

                              {selectedSeedSessionId && (
                                <div className="grid gap-3 md:grid-cols-2">
                                  <div className="md:col-span-2 rounded-2xl border border-[#FFB52B]/30 bg-[#FFF4D9]/70 px-3 py-2 text-sm text-[#8A6100] dark:bg-[#FFB52B]/10 dark:text-[#FFE3A1]">
                                    Seed episode: <span className="font-semibold">{familyAssignment.seedSessionTitle || selectedSeedSessionId}</span>
                                  </div>
                                  <label className="flex flex-col gap-1.5">
                                    <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Family slug</span>
                                    <input
                                      type="text"
                                      value={familyAssignment.familySlug}
                                      onChange={(event) => handleNewFamilyFieldChange('familySlug', event.target.value)}
                                      disabled={isProcessing}
                                      placeholder="guest-topic-family"
                                      className={composerInputClassName}
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1.5">
                                    <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>Family title</span>
                                    <input
                                      type="text"
                                      value={familyAssignment.familyTitle}
                                      onChange={(event) => handleNewFamilyFieldChange('familyTitle', event.target.value)}
                                      disabled={isProcessing}
                                      placeholder="Shared display title"
                                      className={composerInputClassName}
                                    />
                                  </label>
                                  <label className="flex max-w-[180px] flex-col gap-1.5">
                                    <span className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] ${composerMutedTextClassName}`}>New upload version</span>
                                    <input
                                      type="number"
                                      min={2}
                                      value={familyAssignment.familyVersionNumber ?? 2}
                                      onChange={(event) => handleNewFamilyFieldChange('familyVersionNumber', event.target.value)}
                                      disabled={isProcessing}
                                      className={composerInputClassName}
                                    />
                                  </label>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {!hasSelection ? (
                      <>
                        {inputMode === 'file' && (
                          <div
                            {...getRootProps()}
                            className={`
                              relative cursor-pointer rounded-[26px] border-2 border-dashed p-10 text-center transition-all duration-300
                              ${isDragActive && !isDragReject
                                ? 'border-[#FFB52B]/70 bg-[#FFF4D9]/55 dark:bg-[#FFB52B]/12'
                                : isDragReject
                                  ? 'border-red-500/70 bg-red-50 dark:bg-red-900/20'
                                  : isDarkMode
                                    ? 'border-white/[0.14] bg-white/[0.05] hover:border-[#FFB52B]/45 hover:bg-white/[0.08]'
                                    : 'border-[#D6DCE4] bg-white/[0.55] hover:border-[#FFB52B]/45 hover:bg-white/[0.82]'
                              }
                              ${isProcessing ? 'pointer-events-none opacity-50' : ''}
                            `}
                          >
                            <input {...getInputProps()} />
                            <div className="flex flex-col items-center justify-center gap-4">
                              <motion.div animate={isDragActive ? { scale: 1.08 } : { scale: 1 }} transition={{ duration: 0.2 }}>
                                <Upload
                                  size={48}
                                  className={isDragReject ? 'text-red-500' : 'text-brand-yellow'}
                                />
                              </motion.div>
                              <div>
                                <p className="font-headlines text-xl font-semibold">
                                  {isDragActive
                                    ? (isDragReject ? 'Invalid file type' : 'Drop your audio file here')
                                    : 'Upload your audio file'}
                                </p>
                                <p className={`mt-2 font-body text-sm ${composerMutedTextClassName}`}>
                                  Drag and drop or click to browse.
                                </p>
                                <p className={`mt-2 font-body text-xs ${composerMutedTextClassName}`}>
                                  Supports MP3, WAV, M4A, FLAC, MP4, WEBM up to 1GB.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {inputMode === 'youtube' && (
                          <div className={`${composerPanelClassName} p-6 sm:p-7`}>
                            <div className="mx-auto max-w-3xl">
                              <div className="mb-5 flex items-start gap-4">
                                <div className="rounded-2xl bg-red-500/10 p-3">
                                  <Youtube size={28} className="text-red-500" />
                                </div>
                                <div>
                                  <p className="font-headlines text-lg font-semibold">Create from YouTube</p>
                                  <p className={`mt-1 font-body text-sm leading-6 ${composerMutedTextClassName}`}>
                                    Paste a YouTube URL and ReScript will fetch the audio, preserve the source link, and start the ingest pipeline.
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col gap-3 sm:flex-row">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    className={`w-full pr-10 ${composerInputClassName}`}
                                    disabled={isProcessing}
                                    onKeyDown={(e) => e.key === 'Enter' && handleYoutubeSubmit()}
                                  />
                                  <Link size={18} className={`absolute right-3 top-1/2 -translate-y-1/2 ${composerMutedTextClassName}`} />
                                </div>
                                <button
                                  onClick={handleYoutubeSubmit}
                                  disabled={isProcessing || !youtubeUrl.trim()}
                                  className="rounded-full bg-[#FFB52B] px-6 py-3 font-headlines text-sm font-semibold text-[#232E3F] shadow-[0_20px_34px_-24px_rgba(255,181,43,0.92)] transition hover:-translate-y-0.5 hover:bg-[#FFC54D] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Process
                                </button>
                              </div>
                              <div className="mt-4">
                                <button
                                  type="button"
                                  onClick={() => setShowCookiesInput(!showCookiesInput)}
                                  className={`font-body text-sm underline ${isDarkMode ? 'text-[#FFE3A1]' : 'text-[#355E9A]'}`}
                                >
                                  {showCookiesInput ? 'Hide' : 'Show'} YouTube cookies (optional)
                                </button>
                                {showCookiesInput && (
                                  <div className="mt-3">
                                    <textarea
                                      value={youtubeCookies}
                                      onChange={(e) => setYoutubeCookies(e.target.value)}
                                      placeholder="# Netscape HTTP Cookie File&#10;.youtube.com TRUE / TRUE 1750423095 SOCS CAE..."
                                      className={`w-full ${composerInputClassName} font-mono text-xs`}
                                      rows={4}
                                      disabled={isProcessing}
                                    />
                                    <p className={`mt-2 font-body text-xs ${composerMutedTextClassName}`}>
                                      Paste exported cookies here only if the video needs them.
                                      <a
                                        href="https://github.com/yt-dlp/yt-dlp/wiki/Extractors#exporting-youtube-cookies"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-1 underline"
                                      >
                                        Learn how to export cookies
                                      </a>
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {inputMode === 'dropbox' && (
                          <div className={`${composerPanelClassName} p-6 sm:p-7`}>
                            <div className="mb-5 flex items-start gap-4">
                              <div className="rounded-2xl bg-[#FFF4D9] p-3 dark:bg-brand-yellow/12">
                                <Clapperboard size={28} className="text-brand-yellow" />
                              </div>
                              <div>
                                <p className="font-headlines text-lg font-semibold">Create from Dropbox video</p>
                                <p className={`mt-1 max-w-3xl font-body text-sm leading-6 ${composerMutedTextClassName}`}>
                                  ReScript extracts aligned audio from the source video, creates the transcript from that audio, and queues AWS proxy preparation in the background.
                                </p>
                              </div>
                            </div>

                            <ProductionMetadataFields
                              values={{
                                ...dropboxDraft,
                                videoFilename: dropboxDraft.videoFilename || '',
                                productionNotes: dropboxDraft.productionNotes || '',
                              }}
                              onChange={(updates) => setDropboxDraft((current) => ({ ...current, ...updates }))}
                              isDarkMode={isDarkMode}
                              showAutoPrepareToggle
                              compact
                            />

                            <div className="mt-5 flex justify-end">
                              <button
                                onClick={handleDropboxSubmit}
                                disabled={isProcessing || !dropboxDraft.dropboxVideoUrl.trim()}
                                className="rounded-full bg-[#FFB52B] px-6 py-3 font-headlines text-sm font-semibold text-[#232E3F] shadow-[0_20px_34px_-24px_rgba(255,181,43,0.92)] transition hover:-translate-y-0.5 hover:bg-[#FFC54D] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Process
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`${composerPanelClassName} flex items-center justify-between p-4 sm:p-5`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={`rounded-2xl p-2.5 ${isDarkMode ? 'bg-white/[0.08]' : 'bg-[#FFF4D9]'}`}>
                            {selectedFile ? (
                              <File size={22} className="text-brand-yellow" />
                            ) : selectedDropboxDraft ? (
                              <DropboxIcon size={22} className="text-brand-yellow" />
                            ) : (
                              <Youtube size={22} className="text-red-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-headlines text-base font-semibold">{selectedItemTitle}</p>
                            <p className={`truncate font-body text-sm ${composerMutedTextClassName}`}>{selectedItemDetail}</p>
                            {selectedDropboxDraft ? (
                              <p className={`mt-1 truncate font-body text-xs ${composerMutedTextClassName}`}>
                                Timeline: {selectedDropboxDraft.timelineName} · Local path: {selectedDropboxDraft.localVideoPath}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        {!isProcessing && (
                          <button
                            onClick={removeSelection}
                            className={`rounded-full p-2 transition-colors ${isDarkMode ? 'hover:bg-red-500/12' : 'hover:bg-red-50'}`}
                            title="Remove selection"
                          >
                            <X size={20} className="text-red-500" />
                          </button>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-center space-x-2"
          >
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FileUpload;
